import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ComplianceMatrix = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const complianceData = [
    {
      id: 'epa-air',
      category: 'Environmental',
      regulation: 'EPA Air Quality Standards',
      framework: 'EPA',
      status: 'compliant',
      score: 98.5,
      lastAudit: '2024-07-15',
      nextReview: '2024-10-15',
      requirements: 12,
      met: 12,
      actions: 0,
      risk: 'low',
      description: 'Clean Air Act compliance for manufacturing emissions'
    },
    {
      id: 'osha-safety',
      category: 'Safety',
      regulation: 'OSHA Workplace Safety',
      framework: 'OSHA',
      status: 'compliant',
      score: 94.2,
      lastAudit: '2024-06-20',
      nextReview: '2024-09-20',
      requirements: 18,
      met: 17,
      actions: 1,
      risk: 'low',
      description: 'Occupational safety and health standards'
    },
    {
      id: 'dot-transport',
      category: 'Transportation',
      regulation: 'DOT Transportation Safety',
      framework: 'DOT',
      status: 'minor-issues',
      score: 87.3,
      lastAudit: '2024-08-01',
      nextReview: '2024-11-01',
      requirements: 15,
      met: 13,
      actions: 2,
      risk: 'medium',
      description: 'Department of Transportation safety regulations'
    },
    {
      id: 'iso-14001',
      category: 'Environmental',
      regulation: 'ISO 14001 Environmental Management',
      framework: 'ISO',
      status: 'compliant',
      score: 96.8,
      lastAudit: '2024-05-10',
      nextReview: '2025-05-10',
      requirements: 20,
      met: 19,
      actions: 1,
      risk: 'low',
      description: 'Environmental management system standards'
    },
    {
      id: 'carb-emissions',
      category: 'Environmental',
      regulation: 'CARB Emissions Standards',
      framework: 'CARB',
      status: 'at-risk',
      score: 78.9,
      lastAudit: '2024-07-30',
      nextReview: '2024-10-30',
      requirements: 8,
      met: 6,
      actions: 3,
      risk: 'high',
      description: 'California Air Resources Board vehicle emissions'
    },
    {
      id: 'sec-climate',
      category: 'Financial',
      regulation: 'SEC Climate Disclosure',
      framework: 'SEC',
      status: 'in-progress',
      score: 65.4,
      lastAudit: '2024-08-10',
      nextReview: '2024-12-31',
      requirements: 10,
      met: 7,
      actions: 4,
      risk: 'high',
      description: 'Securities and Exchange Commission climate risk disclosure'
    }
  ];

  const categories = [
    { value: 'all', label: 'All Categories', count: complianceData?.length },
    { value: 'Environmental', label: 'Environmental', count: complianceData?.filter(item => item?.category === 'Environmental')?.length },
    { value: 'Safety', label: 'Safety', count: complianceData?.filter(item => item?.category === 'Safety')?.length },
    { value: 'Transportation', label: 'Transportation', count: complianceData?.filter(item => item?.category === 'Transportation')?.length },
    { value: 'Financial', label: 'Financial', count: complianceData?.filter(item => item?.category === 'Financial')?.length }
  ];

  const getStatusConfig = (status) => {
    switch (status) {
      case 'compliant':
        return { color: 'text-success', bg: 'bg-success/10', icon: 'CheckCircle', label: 'Compliant' };
      case 'minor-issues':
        return { color: 'text-warning', bg: 'bg-warning/10', icon: 'AlertTriangle', label: 'Minor Issues' };
      case 'at-risk':
        return { color: 'text-error', bg: 'bg-error/10', icon: 'AlertCircle', label: 'At Risk' };
      case 'in-progress':
        return { color: 'text-primary', bg: 'bg-primary/10', icon: 'Clock', label: 'In Progress' };
      default:
        return { color: 'text-muted-foreground', bg: 'bg-muted/10', icon: 'Circle', label: 'Unknown' };
    }
  };

  const getRiskConfig = (risk) => {
    switch (risk) {
      case 'low':
        return { color: 'text-success', bg: 'bg-success/10', label: 'Low Risk' };
      case 'medium':
        return { color: 'text-warning', bg: 'bg-warning/10', label: 'Medium Risk' };
      case 'high':
        return { color: 'text-error', bg: 'bg-error/10', label: 'High Risk' };
      default:
        return { color: 'text-muted-foreground', bg: 'bg-muted/10', label: 'Unknown' };
    }
  };

  const filteredData = selectedCategory === 'all' 
    ? complianceData 
    : complianceData?.filter(item => item?.category === selectedCategory);

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
        <div className="flex items-center space-x-3 mb-4 lg:mb-0">
          <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
            <Icon name="Shield" size={20} color="var(--color-primary)" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Regulatory Compliance Matrix</h3>
            <p className="text-sm text-muted-foreground">Comprehensive compliance tracking across all regulations</p>
          </div>
        </div>

        <Button variant="outline" iconName="FileText" iconPosition="left">
          Generate Report
        </Button>
      </div>
      {/* Category Filter */}
      <div className="flex flex-wrap gap-2 mb-6">
        {categories?.map((category) => (
          <button
            key={category?.value}
            onClick={() => setSelectedCategory(category?.value)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg border transition-colors ${
              selectedCategory === category?.value
                ? 'border-primary bg-primary/5 text-primary' :'border-border bg-muted/50 text-muted-foreground hover:text-foreground hover:bg-muted'
            }`}
          >
            <span className="font-medium">{category?.label}</span>
            <span className="text-xs bg-background px-2 py-1 rounded-full">{category?.count}</span>
          </button>
        ))}
      </div>
      {/* Compliance Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-3 px-4 font-medium text-muted-foreground">Regulation</th>
              <th className="text-left py-3 px-4 font-medium text-muted-foreground">Status</th>
              <th className="text-left py-3 px-4 font-medium text-muted-foreground">Score</th>
              <th className="text-left py-3 px-4 font-medium text-muted-foreground">Requirements</th>
              <th className="text-left py-3 px-4 font-medium text-muted-foreground">Risk Level</th>
              <th className="text-left py-3 px-4 font-medium text-muted-foreground">Next Review</th>
              <th className="text-left py-3 px-4 font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredData?.map((item) => {
              const statusConfig = getStatusConfig(item?.status);
              const riskConfig = getRiskConfig(item?.risk);
              
              return (
                <tr key={item?.id} className="border-b border-border hover:bg-muted/30 transition-colors">
                  <td className="py-4 px-4">
                    <div>
                      <div className="font-medium text-foreground">{item?.regulation}</div>
                      <div className="text-sm text-muted-foreground">{item?.description}</div>
                      <div className="flex items-center space-x-2 mt-1">
                        <span className="text-xs bg-muted px-2 py-1 rounded">{item?.framework}</span>
                        <span className="text-xs bg-muted px-2 py-1 rounded">{item?.category}</span>
                      </div>
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <div className={`flex items-center space-x-2 px-3 py-1 rounded-full ${statusConfig?.bg} w-fit`}>
                      <Icon name={statusConfig?.icon} size={14} color={statusConfig?.color?.replace('text-', 'var(--color-')} />
                      <span className={`text-sm font-medium ${statusConfig?.color}`}>
                        {statusConfig?.label}
                      </span>
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex items-center space-x-2">
                      <span className="text-lg font-bold text-foreground">{item?.score}%</span>
                      <div className="w-16 bg-muted rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${
                            item?.score >= 95 ? 'bg-success' :
                            item?.score >= 85 ? 'bg-warning' : 'bg-error'
                          }`}
                          style={{ width: `${item?.score}%` }}
                        ></div>
                      </div>
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <div className="text-sm">
                      <span className="font-medium text-foreground">{item?.met}</span>
                      <span className="text-muted-foreground"> of {item?.requirements}</span>
                    </div>
                    <div className="text-xs text-muted-foreground">requirements met</div>
                  </td>
                  <td className="py-4 px-4">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${riskConfig?.bg} ${riskConfig?.color}`}>
                      {riskConfig?.label}
                    </span>
                  </td>
                  <td className="py-4 px-4">
                    <div className="text-sm text-foreground">{formatDate(item?.nextReview)}</div>
                    <div className="text-xs text-muted-foreground">
                      Last: {formatDate(item?.lastAudit)}
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    {item?.actions > 0 ? (
                      <div className="flex items-center space-x-2">
                        <span className="flex items-center justify-center w-6 h-6 bg-warning text-white rounded-full text-xs font-bold">
                          {item?.actions}
                        </span>
                        <span className="text-sm text-muted-foreground">pending</span>
                      </div>
                    ) : (
                      <span className="text-sm text-success">All clear</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {/* Summary Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6 pt-6 border-t border-border">
        <div className="text-center">
          <p className="text-2xl font-bold text-success">4</p>
          <p className="text-sm text-muted-foreground">Fully Compliant</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-warning">1</p>
          <p className="text-sm text-muted-foreground">Minor Issues</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-error">1</p>
          <p className="text-sm text-muted-foreground">At Risk</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-primary">88.5%</p>
          <p className="text-sm text-muted-foreground">Avg Score</p>
        </div>
      </div>
    </div>
  );
};

export default ComplianceMatrix;